// Tenant entity (displayed as "Workspace" in UI)
export interface Tenant {
  // Primary identifiers
  tenantCode: string // Primary key, immutable
  tenantName: string // Display name
  workspaceCode: string // Workspace identifier

  // Location & settings
  countryCodes: string // Comma-separated country codes (e.g., "SG,MY,ID")
  timeZoneId: string // IANA timezone (e.g., "Asia/Singapore")

  // Status flags
  isDemo: boolean // Demo workspace flag (legacy; superseded by workspaceType)
  workspaceType: 'new' | 'demo' | 'trial' | 'uat' | null // Workspace type — nullable for pre-backfill rows
  inactiveDate: string | null // ISO 8601 date if inactive, null if active
  destroyDate: string | null // ISO 8601 date for scheduled destruction, null if not set

  // License
  licenseJsonb: string | null // JSON object with module license counts

  // Audit fields
  createdDate: string // ISO 8601 timestamp
  createdBy: string // Username who created workspace

  // Optional fields
  logoId: string | null // UUID reference to logo file

  // Related data
  featureFlags: TenantFeatureFlag[] // Enabled feature flags
  userCount?: number // Total users (computed field)
}

// Feature flag for a tenant
export interface TenantFeatureFlag {
  featureFlag: string // Feature identifier (e.g., "payroll_multicurrency")
  disabledDate: string | null // ISO 8601 date if disabled, null if enabled
}

// Filter state for tenant list
export interface TenantFilters {
  // Search
  searchTerm: string // Partial match against name/code (case-insensitive)

  // Status filters
  showInactive: boolean // Include inactive tenants
  showDemoOnly: boolean // Show only demo tenants

  // Feature flag filters
  requiredFeatureFlags: string[] // Tenants must have all these flags

  // Country filter
  selectedCountries: string[] // Filter by country codes
}

// Sort state for tenant list
export type SortDirection = 'ascend' | 'descend' | null

export interface TenantSortState {
  field: keyof Tenant | null
  direction: SortDirection
}

// API request parameters
export interface ListTenantsParams {
  includeInactive?: boolean // Default: false
  includeFeatureFlags?: boolean // Default: true
  includeUserCount?: boolean // Default: false (expensive query)
}

// API response
export interface ListTenantsResponse {
  data: Tenant[]
  total: number
}

// Default filter state
export const defaultFilters: TenantFilters = {
  searchTerm: '',
  showInactive: false,
  showDemoOnly: false,
  requiredFeatureFlags: [],
  selectedCountries: []
}

// Feature flag display mapping
export const featureFlagLabels: Record<string, string> = {
  // Core modules
  payroll: 'Payroll',
  leave: 'Leave',
  claim: 'Claim',
  attendance: 'Attendance',
  schedule: 'Schedule',
  performance: 'Performance',

  // Employee self-service
  myprofile: 'My Profile',
  mypayroll: 'My Payroll',
  myleave: 'My Leave',
  myclaim: 'My Claim',
  myattendance: 'My Attendance',
  myschedule: 'My Schedule',

  // Feature-specific
  claim_ai: 'Claim AI',

  // People Pro collection
  people_pro: 'People Pro',
  letter: 'Letter',
  my_employee_documents: 'Employee Documents Self-Service',
  performance_conduct: 'Performance & Conduct',
  em_document_bulk: 'Employee Document Bulk Upload',

  // Payroll Pro collection
  payroll_pro: 'Payroll Pro',
  payroll_multicurrency: 'Payroll Multi-Currency',
  general_ledger: 'General Ledger',
  cost_allocation: 'Cost Allocation',
  payroll_bulk: 'Payroll Bulk',
  bonus_generate: 'Bonus Generate',

  // People add-ons
  whatsapp: 'WhatsApp',

  // Payroll add-ons
  payslip_email_pdf: 'Payslip Email with PDF',

  // Attendance add-ons
  kiosk: 'Kiosk',
  daily_amendment: 'Daily Amendment',
  ai_report: 'AI Report Builder'
}

// Feature flag collection mapping (must match backend FEATURE_FLAG_COLLECTION_MAP in Host.Data.Constants.FeatureFlag)
export const featureFlagCollectionMap: Record<string, string[]> = {
  people_pro: ['letter', 'my_employee_documents', 'performance_conduct'],
  payroll_pro: ['payroll_multicurrency', 'general_ledger', 'cost_allocation', 'payroll_bulk', 'bonus_generate']
}

// Reverse lookup: child flag -> parent flag
export const childToParentMap: Record<string, string> = {}
for (const [parent, children] of Object.entries(featureFlagCollectionMap)) {
  for (const child of children) {
    childToParentMap[child] = parent
  }
}

// All child flags (for filtering from dropdown options)
export const allChildFlags = new Set(Object.keys(childToParentMap))

// Format feature flag for display
export function formatFeatureFlag(flag: string): string {
  return (
    featureFlagLabels[flag] ||
    flag
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  )
}

// Feature flag sort order (lower number = higher priority)
const featureFlagSortOrder: Record<string, number> = {
  // Core modules (1-10)
  payroll: 1,
  leave: 2,
  claim: 3,
  attendance: 4,
  schedule: 5,

  // Employee self-service (11-20)
  myprofile: 11,
  mypayroll: 12,
  myleave: 13,
  myclaim: 14,
  myattendance: 15,
  myschedule: 16,
  whatsapp: 17,

  // Feature-specific (21-30)
  claim_ai: 21,

  // People Pro collection (22-25)
  people_pro: 22,
  letter: 23,
  my_employee_documents: 24,
  performance_conduct: 25,

  em_document_bulk: 26,

  // Payroll Pro collection (26-30)
  payroll_pro: 27,
  payroll_multicurrency: 28,
  general_ledger: 29,
  cost_allocation: 30,
  payroll_batch: 31,
  bonus_generate: 32,

  // Payroll add-ons (33+)
  payslip_email_pdf: 33,

  // Attendance add-ons (34+)
  kiosk: 34,
  daily_amendment: 35,

  // AI (36+)
  ai_report: 36
}

// Sort feature flags in logical order
export function sortFeatureFlags(flags: TenantFeatureFlag[]): TenantFeatureFlag[] {
  return [...flags].sort((a, b) => {
    const orderA = featureFlagSortOrder[a.featureFlag] || 999
    const orderB = featureFlagSortOrder[b.featureFlag] || 999

    if (orderA !== orderB) {
      return orderA - orderB
    }

    // If not in sort order, sort alphabetically
    return a.featureFlag.localeCompare(b.featureFlag)
  })
}

// Country code to name mapping
const countryCodeMap: Record<string, string> = {
  sg: 'Singapore',
  my: 'Malaysia',
  id: 'Indonesia',
  th: 'Thailand',
  ph: 'Philippines',
  vn: 'Vietnam',
  us: 'United States',
  gb: 'United Kingdom',
  au: 'Australia',
  nz: 'New Zealand',
  hk: 'Hong Kong',
  tw: 'Taiwan',
  jp: 'Japan',
  kr: 'South Korea',
  cn: 'China',
  in: 'India'
}

// Format country codes for display
export function formatCountryCodes(codes: string): string {
  if (!codes) return '-'

  return codes
    .split(',')
    .map(code => code.trim().toLowerCase())
    .map(code => countryCodeMap[code] || code.toUpperCase())
    .join(', ')
}
